import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Icon, Tooltip } from '@m12s/component-library';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';

import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getApmCurrentPartCountMultiplier } from 'lib/selectors/getApmCurrentPartCountMultiplier';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { getPromptOperatorForPartCountMultiplier } from 'lib/selectors/getPromptOperatorForPartCountMultiplier';
import useSetPartCountMultiplier from 'lib/hooks/useSetPartCountMultiplier';
import {
  PartCountMultiplierDialogBase,
  formatMultiplierLabel,
  formatPartsCyclesRatio,
} from 'components/PartCountMultiplierDialog';

import { OpButton, DisabledPcmTooltip, TopCard } from './styled';

// Always-present edit pencil beside the "Part Counting" label. Editable is an
// indigo button that opens the dialog. When disabled, the pencil itself is the
// trigger for the repo's standard Tooltip (@m12s/component-library). The @m12s
// Tooltip is show-prop controlled, so we drive `show` from BOTH hover and click
// (tap) — matching the MFE drawer's hover+tap behavior — and close on click
// outside.
const PcmEditPencil = ({ state, reason, onEdit, editLabel }) => {
  const [showReason, setShowReason] = useState(false);
  const wrapperRef = useRef();

  const onClickOutside = useCallback((event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setShowReason(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      return document.removeEventListener('mousedown', onClickOutside);
    };
  }, [onClickOutside]);

  if (state === 'editable') {
    return (
      <OpButton aria-label={editLabel} onClick={onEdit}>
        <Icon icon={faEdit} size="0.75rem" />
      </OpButton>
    );
  }

  // The greyed pencil IS the tooltip's icon-button trigger (one element, no
  // doubled icon). DisabledPcmTooltip tames the @m12s button to a compact,
  // greyed, not-allowed footprint so the card layout matches the editable state.
  return (
    <DisabledPcmTooltip
      ref={wrapperRef}
      onMouseEnter={() => {
        return setShowReason(true);
      }}
      onMouseLeave={() => {
        return setShowReason(false);
      }}
    >
      <Tooltip
        icon={faEdit}
        show={showReason}
        aria-label={reason}
        onClick={() => {
          return setShowReason((prev) => {
            return !prev;
          });
        }}
        location="bottom"
      >
        {reason}
      </Tooltip>
    </DisabledPcmTooltip>
  );
};

PcmEditPencil.propTypes = {
  state: PropTypes.oneOf(['editable', 'disabled-gate', 'disabled-nodata'])
    .isRequired,
  reason: PropTypes.string,
  onEdit: PropTypes.func,
  editLabel: PropTypes.string,
};

const PartCountingCard = () => {
  const { t } = useTranslation();
  const latestActivitySet = useSelector(getLatestActivitySet);
  const feedMultiplier = useSelector(getApmCurrentPartCountMultiplier);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);
  const canEdit = useSelector(getPromptOperatorForPartCountMultiplier);
  const setPartCountMultiplier = useSetPartCountMultiplier();

  const [dialogOpen, setDialogOpen] = useState(false);
  // Short-lived optimistic value so the card flips the instant the operator
  // saves, instead of waiting seconds for the feed refetch to land. It takes
  // top precedence, then is cleared once feedMultiplier catches up to it (see
  // the reconcile effect below). We flip the CARD locally rather than reading
  // the optimistic activitySets.byRef write, because that source reverts on the
  // labor-ticket-submit overwrite and can carry a stale WOO — the exact bugs
  // getApmCurrentPartCountMultiplier was added to avoid.
  const [savedPcm, setSavedPcm] = useState(null);

  const { partCountMultiplier } = latestActivitySet || {};
  // The save keys off the WOO, and the server resolves the open run from it.
  // In the APM ERP flow the current run lives in the program-events feed, not
  // in activitySets — so latestActivitySet carries no WOO (and no activitySetRef).
  // Use the feed-elected WOO there, the same source the save hook and
  // getApmCurrentPartCountMultiplier use; fall back to the activity set otherwise.
  const workOrderOperationRef =
    isApmERPJobFlow && currentWorkOrderOp?.workOrderOperationRef != null
      ? currentWorkOrderOp.workOrderOperationRef
      : latestActivitySet?.workOrderOperationRef;
  // Prefer the local optimistic value, then the program-events feed (survives
  // the labor-ticket-submit overwrite that wipes activitySets.byRef); fall back
  // to the activity set otherwise.
  const multiplier = savedPcm ?? feedMultiplier ?? partCountMultiplier ?? 1;

  // Reconcile the optimistic value once the authoritative source reports a
  // concrete value after our save. The source differs by flow: the
  // program-events feed in the APM ERP flow, the activity set otherwise.
  // feedMultiplier is always null outside the APM flow, so keying on it alone
  // would strand savedPcm forever in the non-APM case.
  const reconcileSource = isApmERPJobFlow
    ? feedMultiplier
    : partCountMultiplier;
  useEffect(() => {
    if (savedPcm != null && reconcileSource != null) {
      setSavedPcm(null);
    }
    // Reconcile on reconcileSource changes, not savedPcm, so a fresh save
    // re-arms the optimistic value before the next update lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconcileSource]);

  // Gate off → disabled; gate on but no WOO ref (write keys off the WOO) →
  // disabled; gate on + WOO → editable.
  const pcmEditState = !canEdit
    ? 'disabled-gate'
    : !workOrderOperationRef
    ? 'disabled-nodata'
    : 'editable';

  const handleSave = useCallback(
    async (value) => {
      setDialogOpen(false);
      // Flip the card immediately; on success the reconcile effect clears this
      // once the authoritative source catches up.
      setSavedPcm(value);
      // The hook returns false on a handled failure. We must drop the optimistic
      // value here: in the feed flow a failed save changes nothing server-side,
      // so the feed refetch comes back unchanged and the reconcile effect never
      // fires — the card would otherwise stay stuck on the failed value.
      try {
        const ok = await setPartCountMultiplier(value);
        if (!ok) {
          setSavedPcm(null);
        }
      } catch (error) {
        setSavedPcm(null);
      }
    },
    [setPartCountMultiplier]
  );

  return (
    <>
      <TopCard
        label={t('Part Counting')}
        labelAccessory={
          <PcmEditPencil
            state={pcmEditState}
            editLabel={t('Edit part count method')}
            reason={
              pcmEditState === 'disabled-gate'
                ? t(
                    'Part count editing is turned off for this machine. Ask a manager to enable it in Machine Settings.'
                  )
                : t(
                    "Part counting can't be edited until this machine is running a matched production order operation."
                  )
            }
            onEdit={() => {
              return setDialogOpen(true);
            }}
          />
        }
        value={formatMultiplierLabel(multiplier)}
        subValue={formatPartsCyclesRatio(multiplier)}
      />
      {dialogOpen && (
        <PartCountMultiplierDialogBase
          defaultValue={multiplier}
          onSave={handleSave}
          onCancel={() => {
            return setDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

export default PartCountingCard;
